import { axios } from '@/lib/axios';
import {
    QuizzesResponse,
    CreateQuizData,
    UpdateQuizData,
    QuizLibraryResponse,
    ImportPlaylistResponse,
    QuizLibrary,
    ShareResponse,
    AdminQuizV2ListResponse,
    CustomerQuizV2ListResponse,
    QuizSharedByAdminV2ListResponse,
    MyQuizV2ListResponse,
} from '../types';

import { EndDate, Quiz, RebuildSectionQuizLibraryData, UserResultsResponse } from '@/types';

import { GetListBodyStructure } from '@/types';
import { API_URL } from '@/config';
import FileDownload from 'js-file-download';
import type { QuizV2CreateRequestBody } from '../utils/buildQuizV2CreatePayload';
import type { AdminQuizDetailsContent, QuizV2UpdateRequestBody } from '../utils/quizV2AdminEdit';
import type {
    AttemptGradeSyncLogsResponse,
    CustomerQuizAssignmentsListResponse,
    QuizV2AttemptDetailsResponse,
} from '../types';

export const buildQuizzesParams = (
    activePage: number,
    activeSearchKeyword: string | number,
    withAssign: boolean | null = false,
    itemsOnPage = 20
) => {
    return {
        navigation: {
            page: activePage,
            itemsOnPage,
        },
        totalCount: true,
        filter: {
            collection: [
                ...(activeSearchKeyword
                    ? [
                          {
                              bool_operator: 'or',
                              condition: {
                                  property: Number.isInteger(activeSearchKeyword) ? 'id' : 'title',
                                  operator: Number.isInteger(activeSearchKeyword) ? 'equals' : 'contains',
                                  value: Number.isInteger(activeSearchKeyword) ? activeSearchKeyword : activeSearchKeyword || '',
                              },
                          },
                      ]
                    : []),
            ],
        },
        sort: [['id', 'desc']],
        withAssign,
    };
};

export const postQuizzes = (data: GetListBodyStructure): Promise<QuizzesResponse> => {
    return axios.post(`${API_URL}/api/admin/quiz/template_list`, data);
};

export const getQuiz = (id: number): Promise<Quiz> => {
    return axios.get(`${API_URL}/api/admin/quiz/template/${id}`);
};

export const removeTemplateQuiz = (id: number): Promise<void> => {
    return axios.delete(`${API_URL}/api/admin/quiz/template/${id}`);
};

export const createQuiz = (data: CreateQuizData): Promise<Quiz> => {
    return axios.post(`${API_URL}/api/admin/quiz/template/create`, data);
};

export const updateQuiz = (id: number, data: UpdateQuizData): Promise<Quiz> => {
    return axios.patch(`${API_URL}/api/admin/quiz/template/${id}`, data);
};

export const saveQuizImage = (data: FormData): Promise<{ url: string }> => {
    return axios.post(`${API_URL}/api/quiz/upload_image`, data);
};

export const uploadQuizQti = (data: FormData) => {
    return axios.post(`${API_URL}/api/admin/quiz/template/from_package/qti`, data);
};

export const getQuizTemplateByArticleId = (id: number): Promise<Quiz> => {
    return axios.get(`${API_URL}/api/admin/quiz/from_article/${id}`);
};

export const getQuizLibraryById = (id: number): Promise<QuizLibrary> => {
    return axios.get(`${API_URL}/api/admin/quiz/library/${id}`);
};

export const removeQuizLibraryById = (id: number): Promise<void> => {
    return axios.delete(`${API_URL}/api/admin/quiz/library/${id}`);
};

export const createQuizLibrary = (data: CreateQuizData): Promise<QuizLibrary> => {
    return axios.post(`${API_URL}/api/admin/quiz/library/create`, data);
};

export const updateQuizLibrary = (id: number, data: UpdateQuizData): Promise<QuizLibrary> => {
    return axios.patch(`${API_URL}/api/admin/quiz/library/${id}`, data);
};

export const getQuizLibrary = (data: GetListBodyStructure): Promise<QuizLibraryResponse> => {
    return axios.post(`${API_URL}/api/admin/quiz/library_quiz_list`, data);
};

export const getQuizDataByPlaylistId = (id: number): Promise<ImportPlaylistResponse> => {
    return axios.get(`${API_URL}/api/admin/quiz/from_playlist/${id}`);
};

export const getSharedQuizzes = (data: GetListBodyStructure): Promise<QuizLibraryResponse> => {
    return axios.post(`${API_URL}/api/admin/quiz/shared_quiz_list`, data);
};

export const getSharedUserQuizzes = (): Promise<QuizLibrary[]> => {
    return axios.get(`${API_URL}/api/admin/quiz/list_shared_by_me`);
};

export const getMyQuizzes = (data: GetListBodyStructure): Promise<QuizLibraryResponse> => {
    return axios.post(`${API_URL}/api/admin/quiz/user_quiz_list`, data);
};
export const getAdminQuizzes = (data: GetListBodyStructure): Promise<QuizLibraryResponse> => {
    return axios.post(`${API_URL}/api/admin/quiz/quiz_list`, data);
};

export type AdminQuizV2ListRequestBody = {
    page: number;
    size: number;
    sort: [string, string][];
    title?: string;
};

/** Body for the current-user My Quizzes and Shared By Me v2 search endpoints. */
export type CurrentUserQuizzesV2SearchRequestBody = {
    page: number;
    size: number;
    sort: [string, string][];
    title?: string;
};

/** POST api/v2/admin/quiz/my-quizzes/search — current user's quizzes from quiz-svc (UUID-aware). */
export const postMyQuizzesV2Search = (
    body: CurrentUserQuizzesV2SearchRequestBody
): Promise<MyQuizV2ListResponse> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/my-quizzes/search`, body);
};

/** POST api/v2/admin/quiz/shared-quizzes/search — quizzes the current user shared (UUID-aware). */
export const postSharedByMeV2Search = (
    body: CurrentUserQuizzesV2SearchRequestBody
): Promise<QuizSharedByAdminV2ListResponse> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/shared-quizzes/search`, body);
};

export const postAdminQuizzesV2List = (body: AdminQuizV2ListRequestBody): Promise<AdminQuizV2ListResponse> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/list`, body);
};

export const deleteAdminQuizV2 = (quizUuid: string): Promise<void> => {
    return axios.delete(`${API_URL}/api/v2/admin/quiz/delete/${quizUuid}`);
};

export type AdminQuizSharedV2ListRequestBody = {
    page: number;
    size: number;
    sort: [string, string][];
    title?: string;
};

export const postAdminQuizSharedV2List = (body: AdminQuizSharedV2ListRequestBody): Promise<QuizSharedByAdminV2ListResponse> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/shared`, body);
};

export type CustomerQuizzesV2ListRequestBody = {
    page: number;
    size: number;
    sort: [string, string][];
    name?: string;
    showExpired: boolean;
};

export const postCustomerQuizzesV2List = (body: CustomerQuizzesV2ListRequestBody): Promise<CustomerQuizV2ListResponse> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/customerQuizzes/list`, body);
};

export type ShareQuizV2Response = {
    sharedQuizzes?: { quizUuid?: string }[];
    alreadySharedQuizzes?: { quizUuid?: string }[];
    notFound?: string[];
    notAccess?: string[];
};

export const shareAdminQuizV2 = (quizUuid: string, data: { emails: string[] }): Promise<ShareQuizV2Response> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/${quizUuid}/share`, data);
};

/** Same request/response as {@link shareAdminQuizV2}; calls POST .../customerQuiz/{quizUuid}/share (quiz-svc customer quiz). */
export const shareCustomerQuizV2 = (quizUuid: string, data: { emails: string[] }): Promise<ShareQuizV2Response> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/customerQuiz/${quizUuid}/share`, data);
};

/** GET api/v2/admin/quiz/customerQuiz/{quizUuid}/assignments — proxied quiz-svc list + Symfony user/class enrichment. */
export const adminListAssignmentsOfCustomerQuiz = (
    quizUuid: string,
    params?: { page?: number; size?: number; sortBy?: string; sortOrder?: string }
): Promise<CustomerQuizAssignmentsListResponse> => {
    return axios.get(`${API_URL}/api/v2/admin/quiz/customerQuiz/${quizUuid}/assignments`, {
        params: {
            page: params?.page ?? 0,
            size: params?.size ?? 20,
            ...(params?.sortBy ? { sortBy: params.sortBy } : {}),
            ...(params?.sortOrder ? { sortOrder: params.sortOrder } : {}),
        },
    });
};

/** GET api/v2/admin/quiz/customerQuiz/{quizUuid}/results/csv — CSV marksheet (blob download). */
export const getCsvMarksheetOfCustomerQuizV2 = (quizUuid: string): Promise<void> => {
    return axios
        .get(`${API_URL}/api/v2/admin/quiz/customerQuiz/${quizUuid}/results/csv`, { responseType: 'blob' })
        .then((response) => {
            FileDownload(response.data, `${quizUuid}_results.csv`);
        });
};

/** GET api/quiz/v2/instructor/attempts/{attemptUuid} — attempt breakdown (proxied + assignee user). */
export const getQuizV2AttemptDetails = (attemptUuid: string): Promise<QuizV2AttemptDetailsResponse> => {
    return axios.get(`${API_URL}/api/quiz/v2/instructor/attempts/${attemptUuid}`);
};

/** GET api/v2/admin/quiz/userQuiz/attempt/{attemptUuid}/gradeSyncLogs — LTI grade sync history. */
export const getAttemptGradeSyncLogs = (attemptUuid: string): Promise<AttemptGradeSyncLogsResponse> => {
    return axios.get(`${API_URL}/api/v2/admin/quiz/userQuiz/attempt/${attemptUuid}/gradeSyncLogs`);
};

export const deleteAdminQuizById = (id: number): Promise<void> => {
    return axios.delete(`${API_URL}/api/admin/quiz/${id}`);
};

export const getSharedUsersAdminQuizById = (
    id: number
): Promise<{
    id: number;
    sharedList?: {
        user: {
            id: number;
            email: string;
            firstName: string;
            lastName: string;
        };
    }[];
    assignList?: {
        user: {
            id: number;
            email: string;
            firstName: string;
            lastName: string;
        };
    }[];
}> => {
    return axios.get(`${API_URL}/api/admin/quiz/shared_with/${id}`);
};

export const getSharedCustomerQuizById = (
    id: number
): Promise<{
    id: number;
    sharedList?: {
        user: {
            id: number;
            email: string;
            firstName: string;
            lastName: string;
        };
    }[];
    assignList?: {
        user: {
            id: number;
            email: string;
            firstName: string;
            lastName: string;
        };
    }[];
}> => {
    return axios.get(`${API_URL}/api/admin/quiz/shared_with_customer/${id}`);
};

export const saveSharedUsersAdminQuizById = (
    id: number,
    data: {
        emails: string[];
        isAdminQuiz?: boolean;
    }
): Promise<ShareResponse> => {
    return axios.post(`${API_URL}/api/admin/quiz/share/${id}`, data);
};

export const saveSharedCustomerQuizById = (
    id: number,
    data: {
        emails: string[];
    }
): Promise<ShareResponse> => {
    return axios.post(`${API_URL}/api/admin/quiz/sharecustomer/${id}`, data);
};

export const getAdminQuizById = (id: number): Promise<QuizLibrary> => {
    return axios.get(`${API_URL}/api/admin/quiz/${id}`);
};

export const createAdminQuiz = (data: CreateQuizData): Promise<QuizLibrary> => {
    return axios.post(`${API_URL}/api/admin/quiz/create`, data);
};

export const createAdminQuizV2 = (data: QuizV2CreateRequestBody): Promise<unknown> => {
    return axios.post(`${API_URL}/api/v2/admin/quiz/create`, data);
};

export const getAdminQuizDetails = (quizUuid: string): Promise<AdminQuizDetailsContent> => {
    return axios.get(`${API_URL}/api/v2/admin/quiz/details/${quizUuid}`);
};

/** Quiz V2 customer (instructor) quiz — GET details by UUID (proxied quiz-svc). */
export const getUserQuizDetails = (quizUuid: string): Promise<AdminQuizDetailsContent> => {
    return axios.get(`${API_URL}/api/v2/admin/quiz/userQuiz/${quizUuid}`);
};

export const updateAdminQuizV2 = (quizUuid: string, data: QuizV2UpdateRequestBody): Promise<unknown> => {
    return axios.put(`${API_URL}/api/v2/admin/quiz/update/${quizUuid}`, data);
};

export const updateUserQuizV2 = (quizUuid: string, data: QuizV2UpdateRequestBody): Promise<unknown> => {
    return axios.put(`${API_URL}/api/v2/admin/quiz/update/userQuiz/${quizUuid}`, data);
};

export const updateAdminQuiz = (id: number, data: UpdateQuizData): Promise<QuizLibrary> => {
    return axios.patch(`${API_URL}/api/admin/quiz/${id}`, data);
};

export const getUserQuizById = (id: number): Promise<QuizLibrary> => {
    return axios.get(`${API_URL}/api/admin/quiz/user_quiz/${id}`);
};

export const createUserQuiz = (data: CreateQuizData): Promise<QuizLibrary> => {
    return axios.post(`${API_URL}/api/quiz/create`, data);
};

export const updateUserQuiz = (id: number, data: UpdateQuizData): Promise<QuizLibrary> => {
    return axios.patch(`${API_URL}/api/admin/quiz/user_quiz/${id}`, data);
};

export const getCustomerResults = (id: string): Promise<Quiz> => {
    return axios.get(`${API_URL}/api/admin/quiz/user_quiz/${id}/assign`);
};

export const getCustomerUserResults = (id: string): Promise<UserResultsResponse> => {
    return axios.get(`${API_URL}/api/admin/quiz/assign/${id}`);
};

export const deleteQuiz = (id: number): Promise<void> => {
    return axios.delete(`${API_URL}/api/admin/quiz/user_quiz/${id}`);
};

export const getQuizResultCSV = (id: string): Promise<void> => {
    return axios.get(`${API_URL}/api/admin/quiz/user_quiz/${id}/assign?format=csv`, { responseType: 'blob' }).then((response) => {
        FileDownload(response.data, `${id}_results.csv`);
    });
};

export const updateEndDate = (id: number, data: EndDate): Promise<string> => {
    return axios.post(`${API_URL}/api/admin/quiz/set_end_date/${id}`, data);
};

export const rebuildQuizLibraryById = (id: number): Promise<QuizLibrary> => {
    return axios.get(`${API_URL}/api/admin/quiz/update_section_quiz_library/${id}`);
};

export const rebuildSectionQuizLibrary = (data: RebuildSectionQuizLibraryData): Promise<string> => {
    return axios.post(`${API_URL}/api/admin/quiz/process_section_quiz_libraries`, data);
};
